import { useCurrentNavigationValues } from '@innovamat/ga-features';
import { snack } from '@innovamat/glimmer-components';
import type { ClassroomBody } from '@innovamat/glow-api-client';
import { useClassroomQuery } from '@innovamat/glow-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../../hooks/use-organization';
import { usePutClassroom } from '../../../hooks/use-put-classroom';
import { DIGITAL_PRACTICE_ACCESS } from '../../../modules/classroom/classroom-home-access';

type UseDisableDigitalPracticeModalReturn = {
  isModalOpen: boolean;
  isPending: boolean;
  handleOpenModal: () => void;
  handleCloseModal: () => void;
  handleConfirm: () => void;
};

function useDisableDigitalPracticeModal(): UseDisableDigitalPracticeModalReturn {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invalidatingCache, setInvalidatingCache] = useState(false);

  const queryClient = useQueryClient();
  const { currentClassroom } = useCurrentNavigationValues();
  const { organization } = useOrganization();

  const { data: classroomQuery } = useClassroomQuery(
    {
      id: currentClassroom?.id!,
    },
    {
      enabled: !!currentClassroom?.id,
    }
  );

  const handleOnSuccess = async (): Promise<void> => {
    setInvalidatingCache(true);

    await queryClient.refetchQueries({
      queryKey: useClassroomQuery.getKey({
        id: currentClassroom?.id!,
      }),
    });

    snack.success(t('common.toast.success'));
    setInvalidatingCache(false);
    setIsModalOpen(false);
  };

  const { handlePutClassroom, isPending } = usePutClassroom({
    onSuccess: handleOnSuccess,
    onError: () => {
      snack.error(t('common.toast.error'));
    },
  });

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    if (!isPending && !invalidatingCache) {
      setIsModalOpen(false);
    }
  };

  const handleConfirm = (): void => {
    if (!classroomQuery?.classroom) return;

    const classroom = classroomQuery.classroom;
    const body: ClassroomBody = {
      id: classroom.id,
      avatar: classroom.avatar,
      courseId: classroom.courseId,
      icon: classroom.icon,
      level: classroom.level,
      name: classroom.name,
      organizationId: organization?.id!,
      scheduleEnd: classroom.scheduleEnd,
      scheduleStart: classroom.scheduleStart,
      type: classroom.type,
    };

    handlePutClassroom(
      body,
      undefined,
      DIGITAL_PRACTICE_ACCESS.NO_ACCESS,
      true
    );
  };

  return {
    isModalOpen,
    isPending: isPending || invalidatingCache,
    handleCloseModal,
    handleConfirm,
    handleOpenModal,
  };
}

export { useDisableDigitalPracticeModal };
